import React, { useState, useEffect } from "react";
import { FloatingChat } from "../../search/components/FloatingChat";
import { useParams, useNavigate } from "react-router-dom";
import { FiSearch, FiExternalLink, FiCalendar, FiClock, FiCheckCircle, FiShield, FiGrid } from "react-icons/fi";
import { Regulation, RegulationHistory } from "../types";
import { useTheme } from "../../../context/ThemeContext";
import {
    getRegulationDetail,
    ArtifactTextItem,
} from "../../../api/searchApi";
import PdfViewer from "../../../components/PdfViewer/PdfViewer";
import styles from "./RegulationDetailPage.module.css";
import MarkdownIt from "markdown-it";
import { BRAND_BLUE_SOFT, BRAND_ORANGE } from '../../../design-system/chartColors';

export const RegulationDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [regulation, setRegulation] = useState<Regulation | null>(null);
    const [selectedHistory, setSelectedHistory] =
        useState<RegulationHistory | null>(null);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [artifactTexts, setArtifactTexts] = useState<ArtifactTextItem[]>([]);
    const [selectedArtifactIndex, setSelectedArtifactIndex] = useState(0);
    const [documentType, setDocumentType] = useState<string | null>(null);
    const [regulatoryMeta, setRegulatoryMeta] = useState<{
        status: string | null;
        binding_nature: string | null;
        target_entities: string | null;
        sector_applicability: string | null;
    } | null>(null);

    const FORMAT_SUFFIX_MAP: Record<string, string> = {
        TXT: "Text Document",
        PDF: "PDF Document",
        HTML: "Web Document",
        XML: "XML Document",
        DOC: "Word Document",
    };

    const formatDocLabel = (label: string) => {
        // Prefer the expanded parenthetical when it's longer than the base text
        // e.g. "Hr (House Of Representatives)" → "House Of Representatives"
        const parenMatch = label.match(/\(([^)]+)\)/);
        const base = label.replace(/\s*\([^)]*\)/g, "").trim();
        if (parenMatch && parenMatch[1].length > base.length) {
            return parenMatch[1];
        }
        const parts = base.split("_");
        const last = parts[parts.length - 1].toUpperCase();
        if (FORMAT_SUFFIX_MAP[last] && parts.length > 1) {
            return FORMAT_SUFFIX_MAP[last];
        }
        return base
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getFlag = (code: string | null | undefined): string => {
        if (!code) return "";
        const upper = code.toUpperCase().slice(0, 2);
        return upper.replace(/[A-Z]/g, (c) =>
            String.fromCodePoint(c.charCodeAt(0) - 65 + 0x1f1e6)
        );
    };

    const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const getArtifactLabel = (textType: string) => {
        const normalized = textType.toLowerCase();
        if (normalized === "introduced_in_senate")
            return "Introduced in Senate";
        if (normalized === "reported_to_senate") return "Reported to Senate";

        // Fallback: convert snake_case to Title Case
        return textType
            .replace(/_/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase()
            .replace(/^(\w)|\s(\w)/g, (m) => m.toUpperCase());
    };

    const convertPlainTextToHtml = (text: string) => {
        const escapeHtml = (value: string) =>
            value
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");

        // Normalize newlines and drop leading metadata/header lines like
        // [Congressional Bills...], [From the U.S. Government Publishing Office], <DOC>, etc.
        const rawLines = text.replace(/\r\n/g, "\n").split("\n");
        let start = 0;
        while (
            start < rawLines.length &&
            (/^\s*\[.*\]\s*$/.test(rawLines[start]) || // bracketed metadata lines
                rawLines[start].trim() === "<DOC>" ||
                rawLines[start].trim() === "")
        ) {
            start += 1;
        }

        // Trim trailing footer markers like "<all>" and empty lines
        let end = rawLines.length;
        while (
            end > start &&
            (rawLines[end - 1].trim() === "" ||
                /^<all>$/i.test(rawLines[end - 1].trim()))
        ) {
            end -= 1;
        }

        // Remove lines that are marked as <DELETED> ... </DELETED> in GPO-style texts
        const cleanedLines: string[] = [];
        for (let i = start; i < end; i += 1) {
            const originalLine = rawLines[i];
            const trimmedLine = originalLine.trim();

            // Skip any line that is entirely a DELETED marker block
            if (
                trimmedLine.startsWith("<DELETED>") ||
                trimmedLine.startsWith("</DELETED>")
            ) {
                continue;
            }

            // Also strip inline DELETED tags if they appear around content
            const withoutTags = originalLine.replace(/<\/?DELETED>/g, "");
            cleanedLines.push(withoutTags);
        }

        const body = cleanedLines.join("\n");
        const structuredBody = body
            .replace(/\u00a0/g, " ")
            .replace(/\s*(Table\s+of\s+Contents)\s*/gi, "\n\n$1\n")
            .replace(/\s*((?:Chapter)\s+[IVXLC]+\b)/gi, "\n\n$1")
            .replace(/\s*((?:Section)\s+[IVXLC]+\b)/gi, "\n$1")
            .replace(/\s*((?:Article)\s+\d+\b)/gi, "\n\n$1")
            .replace(/\s*((?:SEC\.|Section)\s+\d+\.?\b)/gi, "\n\n$1")
            // Break before PART headings mid-line (e.g. "...19. PART II: LEGAL EFFECT")
            .replace(/\s+(PART\s+[IVXLC]+\s*[:–—])/g, "\n\n$1")
            // Break before Roman numeral list items: "I –", "II –", "III –", "IV –" etc.
            .replace(/;?\s+([IVXLC]+\s+[–—-]\s+)/g, "\n$1")
            .replace(/\s*(\([0-9]+\))/g, "\n$1")
            // Break TOC entries apart: "dots + page-number" followed by more content → newline
            .replace(/(\.{3,}\s*\d+)\s+(?=[^\s])/g, "$1\n")
            // Break before "Phase 1", "Phase 2" etc.
            .replace(/\s+(Phase\s+\d+\b)/g, "\n\n$1")
            // Break before bullet separators (●, •) used as row dividers
            .replace(/\s*([●•])\s*/g, "\n\n$1 ")
            // Break before decimal-numbered sections: "1.1 The ...", "11.2 Where ..."
            .replace(/([.!?])\s+(\d+\.\d+\s+[A-Z\u00C0-\u024F])/g, "$1\n\n$2")
            .replace(
                /([a-z\u00C0-\u024F,)])\s+(\d+\.\d+\s+[A-Z\u00C0-\u024F])/g,
                "$1\n\n$2",
            )
            // Break before standalone numbered sections like "2 Advisory Guidelines" (number space Capital)
            .replace(/([.!?])\s+(\d+\s+[A-Z][a-z])/g, "$1\n\n$2")
            // Also catch numbered items after bullet separators or mid-sentence
            // (removed overly broad lazy-match regex that caused mis-matches)
            // Break before "1. Capital..." style numbered paragraphs appearing mid-text after sentence end
            .replace(/([.!?])\s+(\d+\.\s+[A-Z\u00C0-\u024F])/g, "$1\n$2")
            // Also break when a numbered paragraph follows directly after a word/paren (no sentence period)
            .replace(
                /([a-z\u00C0-\u024F,)])\s+(\d+\.\s+[A-Z\u00C0-\u024F])/g,
                "$1\n$2",
            )
            // Break before a) b) c) sub-items after colon, semicolon, comma or closing paren
            .replace(/([,:;)]|\d)\s+([a-z]\)\s+)/g, "$1\n$2")
            .replace(/\n([A-Za-z])\./g, "\n\n$1.")
            .replace(/[ \t]{2,}/g, " ")
            .replace(/\n{3,}/g, "\n\n")
            .trim();
        const rawSafeBody = escapeHtml(structuredBody);

        // ── Pre-strip PDF multi-column pipe artifacts BEFORE block splitting ──────
        // Strategy: for every paragraph block, only preserve | characters if the block
        // contains a genuine markdown table separator (|---|---| pattern).
        // Otherwise strip ALL | characters — PDF column margins, page markers, etc.
        const stripPipesFromNonTables = (body: string): string => {
            return (
                body
                    // Split into paragraph blocks, process each one
                    .split(/\n{2,}/)
                    .map((block) => {
                        // Does this block contain a genuine markdown table separator row?
                        const hasRealTableSep = /^\|[ \t]*[-:]+[ \t]*\|/m.test(
                            block,
                        );
                        if (hasRealTableSep) return block; // keep as-is for table rendering

                        // Otherwise strip all pipe-related artifacts:
                        return (
                            block
                                // Remove lines that are entirely pipes / dashes / spaces
                                .replace(/^[ \t]*[|\-\s]+[ \t]*$/gm, "")
                                // Strip "--- Page N ---" / "--- Table (Page N) ---" inline markers
                                .replace(
                                    /---\s*(?:Table\s*)?(?:\(Page\s*\d+\))?\s*---/gi,
                                    "",
                                )
                                // Strip ALL remaining | characters (PDF column separators)
                                .replace(/\s*\|\s*/g, " ")
                                .replace(/\s{2,}/g, " ")
                                .trim()
                        );
                    })
                    .filter(Boolean)
                    .join("\n\n")
            );
        };

        const safeBody = stripPipesFromNonTables(rawSafeBody)
            .replace(/\n{3,}/g, "\n\n")
            .trim();

        const blocks = safeBody.split(/\n{2,}/);

        // A block that has no real textual content — secondary safety net after pre-stripping above.
        const isJunkBlock = (block: string): boolean => {
            const t = block.trim();
            // Pure dashes/spaces/pipes with optional page reference markers
            if (/^[-\s|]{3,}$/.test(t)) return true;
            // e.g. "--- Table (Page 4) ---" or "--- Page 3 ---"
            return false;
        };

        // Render lines like "11 | Description | Phase" as a table — kept as fallback
        // (pre-stripping in safeBody handles most PDF column artifacts before we get here)

        const renderListOrParagraphs = (text: string): string => {
            const lines = text
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean);

            if (lines.length === 0) return "";

            // Table of Contents detection — must come before numbered-list check because
            // TOC entries like "1. PART I ... 3" also match the list pattern.
            const tocLinePattern = /\.{3,}\s*\d+\s*$/;
            const tocLines = lines.filter((l) => tocLinePattern.test(l));
            if (tocLines.length >= 2) {
                return lines
                    .map((l) => `<p class="toc-entry">${l}</p>`)
                    .join("");
            }

            const listPattern =
                /^(\([0-9]+\)|\([a-zA-Z]\)|[0-9]+\.|[a-z]\)|[●•]|[IVXLC]+\s+[–—-])\s*/;
            const listLines = lines.filter((l) => listPattern.test(l));

            // Only render as a list when there are 3+ matching items — avoids treating
            // legal numbered paragraphs (e.g. "1. WHEREAS...  2. WHEREAS...") as lists.
            if (listLines.length >= 3) {
                const items: string[] = [];
                let current = "";

                lines.forEach((line) => {
                    if (listPattern.test(line)) {
                        if (current) items.push(current.trim());
                        current = line.replace(listPattern, "");
                    } else if (current) {
                        current += ` ${line}`;
                    } else {
                        // If we haven't started a list yet, treat leading text as part of the first item
                        current = line;
                    }
                });

                if (current) items.push(current.trim());

                const lis = items.map((item) => `<li>${item}</li>`).join("");
                return `<ol>${lis}</ol>`;
            }

            // Fallback: reflow single newlines (often hard-wrapped source lines)
            // into regular paragraph flow, while preserving block breaks handled above.
            // Also strip inline PDF page/table markers like "--- Table (Page 2) ---"
            const reflowed = text
                .replace(/---\s*(Table|Page)?\s*(\(Page\s*\d+\))?\s*---/gi, "")
                .replace(/\|\s*\|\s*\|/g, "") // strip empty pipe triplets
                .replace(/\s*\|\s*$/gm, "") // strip trailing lone pipes
                .replace(/\n+/g, " ")
                .replace(/\s{2,}/g, " ")
                .trim();
            if (!reflowed) return "";
            return `<p>${reflowed}</p>`;
        };

        // All pipe-table and html/table rendering logic removed. Only plain text/HTML rendering remains.
        const htmlBlocks = blocks.map((block) => {
            const trimmed = block.trim();
            if (!trimmed) return "";
            if (isJunkBlock(trimmed)) return "";
            return renderListOrParagraphs(trimmed);
        });

        // Convert plain-text URLs to clickable links (skip URLs already inside an href)
        const linkifyHtml = (html: string): string =>
            html
                // Re-join URLs broken by a space mid-path (e.g. "…/wp-content/ uploads/…")
                .replace(
                    /(https?:\/\/[^\s<>"']*\/)\s+([a-zA-Z0-9_\-./%]+)/g,
                    "$1$2",
                )
                .replace(
                    /(?<!href="|href=')(https?:\/\/[^\s<>"']+)/g,
                    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
                );

        return linkifyHtml(htmlBlocks.filter(Boolean).join(""));
    };

    // Prepare iframe srcDoc content with a injected stylesheet that forces
    // visible overflow and auto height so iframe documents don't create their
    // own internal scrollbars. We try to inject into an existing <head> if
    // present, otherwise wrap the content in a minimal HTML shell.
    const makeSrcDoc = (htmlContent: string | undefined | null) => {
        const content = htmlContent || "";
        const isDark = theme === "dark";
        const bg = isDark ? "#101828" : "#ffffff";
        const fg = isDark ? "#eaecf0" : "#101828";
        const linkColor = isDark ? BRAND_BLUE_SOFT : BRAND_ORANGE;
        const colorScheme = isDark ? "dark" : "light";
        const overrideStyle = `<style>:root{color-scheme:${colorScheme};}html,body{margin:0;padding:0;width:100%;max-width:none;overflow:hidden !important;height:auto !important;max-height:none !important;background-color:${bg} !important;color:${fg} !important;}body{box-sizing:border-box;padding:0.5rem 0.75rem;line-height:1.6;}*,*::before,*::after{box-sizing:inherit;}pre{white-space:pre-wrap !important;word-break:break-word !important;overflow-wrap:anywhere !important;width:100% !important;max-width:none !important;margin:0;background-color:${bg} !important;color:${fg} !important;}a{color:${linkColor};}table{max-width:100%;margin-left:auto;margin-right:auto;}</style>`;

        try {
            if (/(<\/head>)/i.test(content)) {
                return content.replace(/(<\/head>)/i, `${overrideStyle}$1`);
            }
            if (/(<html[^>]*>)/i.test(content)) {
                return content.replace(
                    /(<html[^>]*>)/i,
                    `$1<head>${overrideStyle}</head>`,
                );
            }
        } catch (e) {
            // fallback to wrapping
        }

        return `<!doctype html><html><head>${overrideStyle}</head><body>${content}</body></html>`;
    };

    const resizeIframeToContent = (iframe: HTMLIFrameElement) => {
        try {
            const doc =
                iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) return;

            const updateHeight = () => {
                const documentHeight = Math.max(
                    doc.documentElement?.scrollHeight || 0,
                    doc.body?.scrollHeight || 0,
                    doc.documentElement?.offsetHeight || 0,
                    doc.body?.offsetHeight || 0,
                );

                if (documentHeight > 0) {
                    iframe.style.height = `${documentHeight + 24}px`;
                }
            };

            iframe.style.overflow = "hidden";
            iframe.style.width = "100%";
            iframe.setAttribute("scrolling", "no");

            [0, 50, 150, 400, 1000].forEach((delay) => {
                window.setTimeout(updateHeight, delay);
            });
        } catch (err) {
            // ignore cross-origin or iframe access errors
        }
    };

    const extractPreformattedText = (htmlContent: string): string | null => {
        const match = htmlContent.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i);
        if (!match || !match[1]) return null;

        const decoded = match[1]
            .replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, "$1")
            .replace(/<[^>]+>/g, "")
            .replace(/&nbsp;/gi, " ")
            .replace(/&amp;/gi, "&")
            .replace(/&lt;/gi, "<")
            .replace(/&gt;/gi, ">")
            .replace(/&#39;/gi, "'")
            .replace(/&quot;/gi, '"')
            .trim();

        return decoded.length > 0 ? decoded : null;
    };

    useEffect(() => {
        const fetchRegulation = async () => {
            if (!id) {
                setError("No regulation ID provided");
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const detail = await getRegulationDetail(id);

                // Handle artifact texts if provided
                if (detail.artifact_texts && detail.artifact_texts.length > 0) {
                    setArtifactTexts(detail.artifact_texts);
                    const normalizedIndex = detail.artifact_texts.findIndex(
                        (text) => text.text_type === "normalized_content",
                    );
                    setSelectedArtifactIndex(
                        normalizedIndex >= 0 ? normalizedIndex : 0,
                    );

                    // Extract document type from normalized content JSON
                    const normalizedArt = normalizedIndex >= 0
                        ? detail.artifact_texts[normalizedIndex]
                        : null;
                    if (normalizedArt?.content) {
                        let docType: string | null = null;
                        try {
                            const data = JSON.parse(normalizedArt.content);
                            docType = data.document_type || data.doc_type || null;
                            if (!docType) {
                                for (const sectionContent of Object.values(data.sections || {})) {
                                    if (sectionContent && typeof sectionContent === "object" && !Array.isArray(sectionContent)) {
                                        const obj = sectionContent as Record<string, unknown>;
                                        const key = Object.keys(obj).find((k) => /document\s*type/i.test(k));
                                        if (key && typeof obj[key] === "string") {
                                            docType = (obj[key] as string).replace(/\s*\([^)]*\)/g, "").trim();
                                            break;
                                        }
                                    }
                                    if (typeof sectionContent === "string") {
                                        const match = sectionContent.match(/\|\s*Document\s+Type\s*\|\s*\*{0,2}([^*|\n]+)\*{0,2}/i);
                                        if (match) { docType = match[1].replace(/\s*\([^)]*\)/g, "").trim(); break; }
                                    }
                                }
                            }
                        } catch {
                            // Content is raw markdown — scan directly
                            const match = normalizedArt.content.match(
                                /\|\s*\*{0,2}\s*Document\s+Type\s*\*{0,2}\s*\|\s*\*{0,2}\s*([^*|\n(]+)/i
                            );
                            if (match) {
                                docType = match[1].trim();
                            }
                        }
                        console.log("[DocType]", docType);
                        if (docType) setDocumentType(docType);
                    }
                }

                const normalizedFormat = (
                    detail.content_format ||
                    detail.content_type ||
                    ""
                ).toLowerCase();
                const trimmedContent = detail.content?.trim() || "";
                const loweredContent = trimmedContent.toLowerCase();

                const fallbackSections: string[] = [];
                if (detail.title) {
                    fallbackSections.push(`# ${detail.title}`);
                }
                if (detail.summary) {
                    fallbackSections.push(`## Summary\n${detail.summary}`);
                }
                if (detail.sponsors && detail.sponsors.length > 0) {
                    fallbackSections.push(
                        `## Sponsors\n${detail.sponsors.join(", ")}`,
                    );
                }
                if (detail.committees && detail.committees.length > 0) {
                    fallbackSections.push(
                        `## Committees\n${detail.committees.join(", ")}`,
                    );
                }
                const fallbackContent = fallbackSections.join("\n\n");

                const computedContent =
                    detail.content ||
                    fallbackContent ||
                    `# ${detail.title || "Regulation Details"}\n\nNo additional content available.`;

                const inferredHtmlTokens = [
                    "<!doctype",
                    "<html",
                    "<head",
                    "<meta",
                    "<body",
                    "<style",
                    "<div",
                    "<p",
                    "<table",
                ];
                const hasHtmlTag = /<\/?[a-z][^>]*>/i.test(trimmedContent);
                const isLikelyHtmlDocument =
                    inferredHtmlTokens.some((token) =>
                        loweredContent.startsWith(token),
                    ) ||
                    (/(<html\b|<head\b|<body\b)/i.test(trimmedContent) &&
                        /(<\/html>|<\/body>)/i.test(trimmedContent)) ||
                    /(<meta\b[^>]*>|<style\b[^>]*>)/i.test(trimmedContent);
                const isTextLikeFormat =
                    normalizedFormat.includes("txt") ||
                    normalizedFormat.includes("text") ||
                    normalizedFormat.includes("plain");
                const isHtml =
                    !!trimmedContent &&
                    (isLikelyHtmlDocument || (!isTextLikeFormat && hasHtmlTag));

                const isPdf =
                    !!trimmedContent &&
                    (normalizedFormat.includes("pdf") ||
                        trimmedContent.startsWith("%PDF-"));

                // Convert API response to Regulation type
                const reg: Regulation = {
                    id: detail.id,
                    title: detail.title,
                    content: computedContent,
                    summary: detail.summary,
                    isHtml,
                    isPdf,
                    source: detail.jurisdiction || "Unknown",
                    date: detail.effective_date || detail.action_date || detail.introduced_date || "",
                    jurisdiction:
                        detail.jurisdiction_code || detail.jurisdiction || "",
                    category:
                        detail.bill_type_label ||
                        detail.policy_area ||
                        "General",
                    url: `/regulations/${detail.id}`,
                    externalUrl: detail.source_url || null,
                    rawContent: detail.content,
                    contentFormat: detail.content_format,
                    contentType: detail.content_type,
                    history: [], // History not available from current API
                    billTypeLabel:
                        detail.bill_type_label ||
                        (detail.bill_type
                            ? detail.bill_type.toUpperCase()
                            : null),
                    documentNumber: detail.bill_number || null,
                    introducedDate: detail.introduced_date || null,
                    lastActionDate: detail.action_date || null,
                    effectiveDate: detail.effective_date || null,
                    policyArea: detail.policy_area || null,
                };

                setRegulation(reg);
                // Store regulatory metadata fields
                if (detail.status || detail.binding_nature || detail.target_entities || detail.sector_applicability) {
                    setRegulatoryMeta({
                        status: detail.status || null,
                        binding_nature: detail.binding_nature || null,
                        target_entities: detail.target_entities || null,
                        sector_applicability: detail.sector_applicability || null,
                    });
                }
            } catch (err) {
                console.error("Error fetching regulation:", err);
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to load regulation",
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchRegulation();
    }, [id]);

    const toggleSearch = () => {
        setShowSearch(!showSearch);
        if (showSearch) {
            setSearchQuery("");
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchSubmit = () => {
        const q = searchQuery.trim();
        if (!q) return;
        // Navigate to search results page with query
        navigate(`/search/results?q=${encodeURIComponent(q)}`);
    };

    const handleHistorySelect = (historyItem: RegulationHistory) => {
        setSelectedHistory(historyItem);
        // In a real app, we would load the specific version of the regulation
    };

    const renderContentForArtifact = (
        artifact: ArtifactTextItem,
        regulation: Regulation,
    ) => {
        if (!artifact.content) {
            return <p>No content available for this version</p>;
        }

        const md = new MarkdownIt({
            html: false,
            linkify: true,
            typographer: true,
            breaks: true,
        });

        // Handle normalized content with Markdown rendering
        if (artifact.text_type === "normalized_content") {
            try {
                const data = JSON.parse(artifact.content);
                console.log("Parsed normalized data:", data);

                const sections = Object.entries(data.sections || {}).map(
                    ([title, content]) => (
                        <div key={title} style={{ marginBottom: "2rem" }}>
                            <h2
                                style={{
                                    marginBottom: "1rem",
                                    fontFamily: "var(--ds-font-display)",
                                    letterSpacing: "var(--ds-letter-tight)",
                                    color: "var(--ds-text)",
                                }}
                            >
                                {title}
                            </h2>
                            <div
                                className={styles.markdown}
                                dangerouslySetInnerHTML={{
                                    __html: md.render(content as string),
                                }}
                            />
                        </div>
                    ),
                );

                return <div>{sections}</div>;
            } catch (e) {
                console.error("Error parsing normalized content:", e);
                // Fallback to markdown rendering if JSON parsing fails
                return (
                    <div className={styles.detailHtml}>
                        <div
                            className={styles.markdown}
                            dangerouslySetInnerHTML={{
                                __html: md.render(artifact.content),
                            }}
                        />
                    </div>
                );
            }
        }

        const normalizedFormat = (artifact.text_format || "").toLowerCase();
        const trimmedArtifactContent = artifact.content.trim();
        const loweredArtifactContent = trimmedArtifactContent.toLowerCase();

        const htmlTokens = [
            "<!doctype",
            "<html",
            "<head",
            "<meta",
            "<body",
            "<style",
            "<div",
            "<p",
            "<table",
        ];
        const hasHtmlTag = /<\/?[a-z][^>]*>/i.test(trimmedArtifactContent);
        const isLikelyHtmlDocument =
            htmlTokens.some((token) =>
                loweredArtifactContent.startsWith(token),
            ) ||
            (/(<html\b|<head\b|<body\b)/i.test(trimmedArtifactContent) &&
                /(<\/html>|<\/body>)/i.test(trimmedArtifactContent)) ||
            /(<meta\b[^>]*>|<style\b[^>]*>)/i.test(trimmedArtifactContent);
        const isTextLikeFormat =
            normalizedFormat.includes("txt") ||
            normalizedFormat.includes("text") ||
            normalizedFormat.includes("plain");
        const isHtml =
            !!trimmedArtifactContent &&
            (isLikelyHtmlDocument || (!isTextLikeFormat && hasHtmlTag));

        const isPdf =
            !!trimmedArtifactContent &&
            (normalizedFormat.includes("pdf") ||
                trimmedArtifactContent.startsWith("%PDF-"));

        if (isHtml) {
            const extractedPreText = extractPreformattedText(artifact.content);
            // Only use the plain-text path when the extracted pre content does NOT
            // itself contain HTML markup (i.e. the <pre> held encoded HTML like
            // &lt;meta&gt; which decodes back to real tags — those must go through
            // the iframe renderer instead).
            const preTextHasHtmlTags = extractedPreText
                ? /<[a-z][^>]*>/i.test(extractedPreText)
                : false;
            if (extractedPreText && !preTextHasHtmlTags) {
                return (
                    <div
                        className={styles.detailHtml}
                        dangerouslySetInnerHTML={{
                            __html: convertPlainTextToHtml(extractedPreText),
                        }}
                    />
                );
            }

            return (
                <iframe
                    title={`Regulation HTML - ${artifact.text_type}`}
                    srcDoc={makeSrcDoc(artifact.content)}
                    sandbox="allow-same-origin allow-popups"
                    className={styles.detailIframe}
                    onLoad={(e) =>
                        resizeIframeToContent(
                            e.currentTarget as HTMLIFrameElement,
                        )
                    }
                />
            );
        } else if (isPdf) {
            return (
                <PdfViewer
                    content={artifact.content}
                    sourceUrl={regulation.externalUrl}
                    height={720}
                    className={styles.pdfViewer}
                />
            );
        } else {
            // Render everything as markdown (fallback for plain text formats)
            return (
                <div className={styles.detailHtml}>
                    <div
                        className={styles.markdown}
                        dangerouslySetInnerHTML={{
                            __html: md.render(artifact.content),
                        }}
                    />
                </div>
            );
        }
    };

    if (isLoading) {
        return (
            <div className={styles.regulationDetailPage}>
                <div className={styles.loadingContainer}>
                    <p>Loading regulation details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.regulationDetailPage}>
                <div className={styles.errorContainer}>
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate(-1)}>Go Back</button>
                </div>
            </div>
        );
    }

    if (!regulation) {
        return (
            <div className={styles.regulationDetailPage}>
                <div className={styles.errorContainer}>
                    <h2>Not Found</h2>
                    <p>The requested regulation could not be found.</p>
                    <button onClick={() => navigate("/search")}>
                        Back to Search
                    </button>
                </div>
            </div>
        );
    }

    const hasHistory = regulation.history && regulation.history.length > 0;
    const normalizedArtifact = artifactTexts.find(
        (t) => t.text_type === "normalized_content",
    );
    const selectedArtifact = normalizedArtifact ?? (artifactTexts.length > 0 ? artifactTexts[selectedArtifactIndex] : null);

    return (
        <div
            className={styles.regulationDetailPage}
            data-regulation-detail="true"
        >
            <FloatingChat
                isOpen={isChatOpen}
                onToggle={() => setIsChatOpen(!isChatOpen)}
                context={`regulation:${id}`}
            />
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerLeft}>
                        <h1 className={styles.title}>{regulation.title.replace(/[,،.]+$/, "").trim()}</h1>
                    </div>
                    <div className={styles.headerRight}>
                        <div className={styles.searchContainer}>
                            <button
                                onClick={toggleSearch}
                                className={`${styles.searchButton} ${showSearch ? styles.active : ""}`}
                                aria-label="Search"
                            >
                                <FiSearch className={styles.searchIcon} />
                                {showSearch && (
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={handleSearch}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleSearchSubmit();
                                            }
                                        }}
                                        className={styles.searchInput}
                                        placeholder="Search..."
                                        autoFocus
                                    />
                                )}
                            </button>
                        </div>
                        <div className={styles.actions}>
                            <button
                                className={styles.historyButton}
                                onClick={() =>
                                    navigate(
                                        `/search-history?type=regulation&id=${regulation.id}`,
                                    )
                                }
                            >
                                View Search History
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <div className={styles.contentContainer}>
                {hasHistory && (
                    <aside className={styles.sidebar}>
                        <div className={styles.historySection}>
                            <h3>Version History</h3>
                            <ul className={styles.historyList}>
                                {regulation.history!.map((item) => (
                                    <li
                                        key={item.id}
                                        className={`${styles.historyItem} ${selectedHistory?.id === item.id ? styles.active : ""}`}
                                        onClick={() =>
                                            handleHistorySelect(item)
                                        }
                                    >
                                        <div className={styles.historyVersion}>
                                            v{item.version}
                                        </div>
                                        <div className={styles.historyDate}>
                                            {new Date(
                                                item.date,
                                            ).toLocaleDateString()}
                                        </div>
                                        <div className={styles.historyChanges}>
                                            {item.changes}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                )}

                <main className={styles.mainContent}>
                    {/* Summary + metadata card side by side */}
                    <section className={styles.summarySection}>
                        {regulation.summary && (
                            <div className={styles.summaryBody}>
                                <h2 className={styles.summaryTitle}>Summary</h2>
                                <p
                                    className={styles.summaryText}
                                    dangerouslySetInnerHTML={{
                                        __html: regulation.summary
                                            .replace(/&/g, "&amp;")
                                            .replace(/</g, "&lt;")
                                            .replace(/>/g, "&gt;")
                                            .replace(/&lt;br\s*\/?&gt;/gi, "<br>")
                                            .replace(
                                                /(https?:\/\/[^\s<>"']*\/)\s+([a-zA-Z0-9_\-./%]+)/g,
                                                "$1$2",
                                            )
                                            .replace(
                                                /(https?:\/\/[^\s<>"']+)/g,
                                                '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
                                            ),
                                    }}
                                />
                            </div>
                        )}
                        <div className={styles.metaCard}>
                            {/* Jurisdiction + doc type */}
                            <div className={styles.metaCardJurisdiction}>
                                <div>
                                    <span className={styles.metaCardFlag}>{regulation.source}</span>
                                    {(documentType || regulation.billTypeLabel) && (
                                        <div className={styles.metaCardDocType}>
                                            {documentType || formatDocLabel(regulation.billTypeLabel!)}
                                        </div>
                                    )}
                                </div>
                                <span className={styles.metaCardFlagEmoji}>{getFlag(regulation.jurisdiction)}</span>
                            </div>

                            {/* Status / Binding / Sector rows */}
                            {regulatoryMeta?.status && (
                                <div className={styles.metaCardRow}>
                                    <span className={styles.metaCardRowLabel}>
                                        <FiCheckCircle size={12} className={styles.iconStatus} /> Status
                                    </span>
                                    <span className={styles.metaCardRowValue}>{regulatoryMeta.status}</span>
                                </div>
                            )}
                            {regulatoryMeta?.binding_nature && (
                                <div className={styles.metaCardRow}>
                                    <span className={styles.metaCardRowLabel}>
                                        <FiShield size={12} className={styles.iconBinding} /> Binding
                                    </span>
                                    <span className={styles.metaCardRowValue}>{regulatoryMeta.binding_nature}</span>
                                </div>
                            )}
                            {regulatoryMeta?.sector_applicability && (
                                <div className={styles.metaCardRow}>
                                    <span className={styles.metaCardRowLabel}>
                                        <FiGrid size={12} className={styles.iconSector} /> Sector
                                    </span>
                                    <span className={styles.metaCardRowValue}>{regulatoryMeta.sector_applicability}</span>
                                </div>
                            )}

                            {/* Date rows */}
                            {formatDate(regulation.introducedDate) && (
                                <div className={styles.metaCardRow}>
                                    <span className={styles.metaCardRowLabel}>
                                        <FiCalendar size={12} className={styles.iconPublished} /> {!regulation.lastActionDate && !regulation.effectiveDate ? "Introduced" : "Published"}
                                    </span>
                                    <span className={styles.metaCardRowValue}>{formatDate(regulation.introducedDate)}</span>
                                </div>
                            )}
                            {formatDate(regulation.lastActionDate) && (
                                <div className={styles.metaCardRow}>
                                    <span className={styles.metaCardRowLabel}>
                                        <FiClock size={12} className={styles.iconLastAction} /> Last Action
                                    </span>
                                    <span className={styles.metaCardRowValue}>{formatDate(regulation.lastActionDate)}</span>
                                </div>
                            )}
                            {formatDate(regulation.effectiveDate) && (
                                <div className={styles.metaCardRow}>
                                    <span className={styles.metaCardRowLabel}>
                                        <FiCheckCircle size={12} className={styles.iconEffective} /> Effective
                                    </span>
                                    <span className={styles.metaCardRowValue}>{formatDate(regulation.effectiveDate)}</span>
                                </div>
                            )}

                            {regulation.externalUrl && (
                                <a href={regulation.externalUrl} target="_blank" rel="noopener noreferrer" className={styles.metaCardSource}>
                                    <FiExternalLink size={12} /> View Source
                                </a>
                            )}
                        </div>
                    </section>
                    {/* Content rendering */}
                    {selectedArtifact ? (
                        renderContentForArtifact(selectedArtifact, regulation)
                    ) : regulation.isHtml ? (
                        (() => {
                            const extractedPreText = extractPreformattedText(
                                regulation.content,
                            );
                            const preTextHasHtmlTags = extractedPreText
                                ? /<[a-z][^>]*>/i.test(extractedPreText)
                                : false;
                            if (extractedPreText && !preTextHasHtmlTags) {
                                return (
                                    <div
                                        className={styles.detailHtml}
                                        dangerouslySetInnerHTML={{
                                            __html: convertPlainTextToHtml(
                                                extractedPreText,
                                            ),
                                        }}
                                    />
                                );
                            }

                            return (
                                <iframe
                                    title="Regulation HTML"
                                    srcDoc={makeSrcDoc(regulation.content)}
                                    sandbox="allow-same-origin allow-popups"
                                    className={styles.detailIframe}
                                    onLoad={(e) =>
                                        resizeIframeToContent(
                                            e.currentTarget as HTMLIFrameElement,
                                        )
                                    }
                                />
                            );
                        })()
                    ) : regulation.isPdf ? (
                        <PdfViewer
                            content={regulation.rawContent || null}
                            sourceUrl={regulation.externalUrl}
                            height={720}
                            className={styles.pdfViewer}
                        />
                    ) : (
                        <div
                            className={styles.detailHtml}
                            dangerouslySetInnerHTML={{
                                __html: convertPlainTextToHtml(
                                    regulation.content,
                                ),
                            }}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default RegulationDetailPage;
